import React, { useCallback, useEffect, useRef, useState } from 'react';
import { getAuth } from 'firebase/auth';
import {
    DeleteCastMember,
    GetCastLibrary,
    GetCastMemberUrl,
    UploadCastMember,
} from '../../Client/ManagementServiceClient';

/**
 * Faces you can reuse across projects.
 *
 * Portraits are the one part of a run that cannot be reproduced — the same
 * prompt never draws the same face twice — so a recurring character has to be
 * saved once and pulled back in, rather than regenerated per project.
 */
function CastLibrary({ onError, onNotice }) {
    const [members, setMembers] = useState([]);
    const [urls, setUrls] = useState({});
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [form, setForm] = useState({ name: '', description: '' });
    const fileRef = useRef(null);

    const load = useCallback(async () => {
        try {
            const idToken = await getAuth().currentUser.getIdToken();
            const { status, myJson } = await GetCastLibrary(idToken);
            if (status !== 200) throw new Error(myJson?.detail || 'Could not load your cast library');

            const list = myJson?.CastMembers || [];
            setMembers(list);

            // Portraits live behind signed urls, fetched per member rather than
            // embedded in the list response so the urls stay short-lived.
            const resolved = await Promise.all(list.map(async (member) => {
                const result = await GetCastMemberUrl(idToken, member.CastId);
                return [member.CastId, result.status === 200 ? result.myJson?.Url : ''];
            }));
            setUrls(Object.fromEntries(resolved));
        } catch (err) {
            onError?.(err.message);
        } finally {
            setLoading(false);
        }
    }, [onError]);

    useEffect(() => { load(); }, [load]);

    const upload = async () => {
        const file = fileRef.current?.files?.[0];
        if (!form.name.trim() || !file) {
            onError?.('A name and an image are both needed.');
            return;
        }
        setBusy(true);
        try {
            const idToken = await getAuth().currentUser.getIdToken();
            const { status, myJson } = await UploadCastMember(idToken, {
                name: form.name.trim(),
                description: form.description.trim(),
                file,
            });
            if (status >= 400) throw new Error(myJson?.detail || 'Upload failed');
            setForm({ name: '', description: '' });
            if (fileRef.current) fileRef.current.value = '';
            onNotice?.(`${myJson.Name} added to your cast library.`);
            await load();
        } catch (err) {
            onError?.(err.message);
        } finally {
            setBusy(false);
        }
    };

    const remove = async (member) => {
        if (!window.confirm(`Remove ${member.Name} from the library? Projects already using them keep their copy.`)) return;
        setBusy(true);
        try {
            const idToken = await getAuth().currentUser.getIdToken();
            const { status, myJson } = await DeleteCastMember(idToken, member.CastId);
            if (status >= 400) throw new Error(myJson?.detail || 'Could not remove that entry');
            await load();
        } catch (err) {
            onError?.(err.message);
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="gen-library">
            <div className="gen-library-head">
                <strong>Cast library</strong>
                <span className="gen-muted">Reuse the same faces across projects.</span>
            </div>

            <div className="gen-library-form">
                <div className="gen-field">
                    <label htmlFor="cast-name">Name</label>
                    <input
                        id="cast-name"
                        value={form.name}
                        onChange={(event) => setForm({ ...form, name: event.target.value })}
                        placeholder="e.g. Mara"
                        disabled={busy}
                    />
                </div>
                <div className="gen-field">
                    <label htmlFor="cast-description">How they look</label>
                    <input
                        id="cast-description"
                        value={form.description}
                        onChange={(event) => setForm({ ...form, description: event.target.value })}
                        placeholder="35, weathered, yellow oilskin coat"
                        disabled={busy}
                    />
                </div>
                <div className="gen-field gen-field-narrow">
                    <label htmlFor="cast-file">Image</label>
                    <input id="cast-file" ref={fileRef} type="file" accept="image/*" disabled={busy} />
                </div>
                <button type="button" className="gen-btn" onClick={upload} disabled={busy}>Add</button>
            </div>

            {loading ? (
                <p className="gen-muted">Loading…</p>
            ) : members.length === 0 ? (
                <p className="gen-empty">
                    Nothing saved yet. Add an image above, or use “Save to library” on a portrait you like.
                </p>
            ) : (
                <div className="gen-library-grid">
                    {members.map((member) => (
                        <div key={member.CastId} className="gen-library-card">
                            {urls[member.CastId]
                                ? <img src={urls[member.CastId]} alt={member.Name} />
                                : <div className="gen-portrait-empty">no image</div>}
                            <strong>{member.Name}</strong>
                            {member.Description && <p className="gen-muted">{member.Description}</p>}
                            <button
                                type="button"
                                className="gen-btn gen-btn-sm gen-btn-ghost gen-btn-danger"
                                onClick={() => remove(member)}
                                disabled={busy}
                            >
                                Remove
                            </button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

export { CastLibrary };
